import { Flex, Grid, GridItem, IconButton, Text } from '@chakra-ui/react';
import { ReactComponent as LeftArrowIcon } from 'assets/icons/carousel_arrow_left.svg';
import { UserType } from 'modules/User';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ElementThumbnail } from './ElementThumbnail';
import {
  ImageFrameMenuPack,
  ImageGridMenuPack,
  ListMenuPack,
  SignatureMenuPack,
  TextBlockMenuPack,
} from './ElementsMenu.components.lazy';
import { ELEMENTS_PACKS } from './ElementsMenu.config';
import { ElementsPackTypes } from './ElementsMenu.types';
import QRCodeMenuPack from './ElementsMenuPacks/QRCodePack';

interface ElementsPackViewProps {
  onClickUpgradeModal: (upgradePlan?: UserType) => void;
}

export const ElementsPackView = ({ onClickUpgradeModal }: ElementsPackViewProps) => {
  const [selectedPack, setSelectedPack] = useState<ElementsPackTypes | null>(null);
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.elementsMenu' });
  const onClickOnPack = (selected: ElementsPackTypes) => {
    const validPacks = ELEMENTS_PACKS.map((pack) => pack.id);
    if (!validPacks.includes(selected)) return;
    setSelectedPack(selected);
  };

  const onReturnToPacks = () => {
    setSelectedPack(null);
  };

  return (
    <>
      <Grid templateColumns={'repeat(2, 1fr)'} columnGap={4} rowGap={4} position='relative' paddingRight={4}>
        {ELEMENTS_PACKS.map((pack, index) => {
          const { id, thumbnail } = pack;
          return (
            <GridItem key={index} display={!!selectedPack ? 'none' : 'grid'}>
              <ElementThumbnail
                id={id}
                index={index}
                onClick={() => onClickOnPack(id)}
                thumbnail={thumbnail}
                caption={t(`${`${id.toString()}`}` as keyof typeof t)}
              />
            </GridItem>
          );
        })}
      </Grid>
      <>
        {!!selectedPack && (
          <>
            <Flex alignItems={'center'} h={30} mb={2}>
              <IconButton
                variant='ghost'
                size={'xs'}
                icon={<LeftArrowIcon />}
                aria-label={'Go back to elements menu list'}
                onClick={onReturnToPacks}
                autoFocus={true}
              />
              <Text as='h2' ml={2} fontSize='sm' color='font.500' fontWeight='semibold'>
                {t(`${`${selectedPack.toString()}`}` as keyof typeof t)}
              </Text>
            </Flex>
          </>
        )}
        <Flex overflowY='auto' pt={2}>
          {selectedPack === ElementsPackTypes.TextBlocks && (
            <TextBlockMenuPack onClickUpgradeModal={onClickUpgradeModal} />
          )}
          {selectedPack === ElementsPackTypes.QRCode && <QRCodeMenuPack />}
          {selectedPack === ElementsPackTypes.ImageGrids && (
            <ImageGridMenuPack onClickUpgradeModal={onClickUpgradeModal} />
          )}
          {selectedPack === ElementsPackTypes.Signatures && (
            <SignatureMenuPack onClickUpgradeModal={onClickUpgradeModal} />
          )}
          {selectedPack === ElementsPackTypes.Lists && <ListMenuPack onClickUpgradeModal={onClickUpgradeModal} />}
          {selectedPack === ElementsPackTypes.ImageFrames && (
            <ImageFrameMenuPack onClickUpgradeModal={onClickUpgradeModal} />
          )}
        </Flex>
      </>
    </>
  );
};

ElementsPackView.displayName = 'ElementsPackView';
